import useSWR from "swr"

import { exactCurrencyFormatter } from "../../../utils"
import { getForecastTradeSpend, type TradeSpendRow } from "../api/forecast_catalog_api"
import { format_period_header, parse_period_key_to_time_period } from "../utils/periods"

function dollars(value: number | undefined): string {
  return value ? exactCurrencyFormatter(Math.round(value)) : "—"
}

/** Trade spend by customer and month, read from the promo calendar. Read-only. */
export function TradeSpendPanel({ version_id, className = "" }: { version_id: string; className?: string }) {
  const { data, error, isLoading } = useSWR(["forecast-trade-spend", version_id], () => getForecastTradeSpend(version_id), {
    revalidateOnFocus: false,
  })

  return (
    <section className={`rounded-lg border border-gray-200 bg-white ${className}`} aria-label="Trade spend">
      <header className="border-b border-gray-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-gray-900">Trade spend from the promo calendar</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Each promo's expected spend, spread over its days. Change it on the promo calendar; nothing here is part of the forecast.
          {data?.pending_promos
            ? ` ${data.pending_promos} pending (${dollars(data.pending_amount)}) and cancelled promos are left out.`
            : " Cancelled promos are left out."}
          {data?.promos_without_amount ? ` ${data.promos_without_amount} have no amount on the calendar.` : ""}
        </p>
      </header>
      {isLoading ? <div className="p-4 text-sm text-gray-500">Loading…</div> : null}
      {error ? <div className="p-4 text-sm text-red-600">Could not load trade spend.</div> : null}
      {data && data.rows.length === 0 ? <div className="p-4 text-sm text-gray-500">No promos on the calendar for this year.</div> : null}
      {data && data.rows.length > 0 ? (
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-medium">Customer</th>
                {data.months.map((month: string) => (
                  <th key={month} className="min-w-24 px-3 py-2 text-right font-medium">
                    {format_period_header(parse_period_key_to_time_period(month))}
                  </th>
                ))}
                <th className="min-w-28 px-3 py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row: TradeSpendRow) => (
                <tr key={`${row.customer}|${row.bill_to ?? ""}`} className="border-t border-gray-100">
                  <td className="sticky left-0 bg-white px-3 py-1.5">
                    <div className="whitespace-nowrap font-medium text-gray-900">{row.customer}</div>
                    <div className="whitespace-nowrap text-xs text-gray-500">
                      {row.bill_to ? `Billed to ${row.bill_to} · ` : ""}
                      {row.promo_count} promo{row.promo_count === 1 ? "" : "s"}
                    </div>
                  </td>
                  {data.months.map((month: string) => (
                    <td key={month} className="px-3 py-1.5 text-right tabular-nums text-gray-700">
                      {dollars(row.by_month[month])}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-gray-900">{dollars(row.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="sticky bottom-0 bg-gray-50">
              <tr className="border-t-2 border-gray-300">
                <td className="sticky left-0 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-900">Total</td>
                {data.months.map((month: string) => (
                  <td key={month} className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                    {dollars(data.total_by_month[month])}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">{dollars(data.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}
    </section>
  )
}
